/* eslint-disable import/first */
import './public-path'; // Don't try to re-order this, it needs to be imported before any other modules
import _ from 'underscore';
import React from 'react';
import ReactDOM from 'react-dom';
import { getInstrumentation, InstrumentationSchema } from '@bingads-webui-campaign/instrumentation-setup';
import {
  OdataErrorMapper,
  CampaignApiErrorMapper,
  UrlErrorMapper,
  odataErrorCodes,
  campaignApiErrorCodes,
  invalidDomains,
} from '@bingads-webui-campaign/instrumentation';
import { setCampaignType } from '@bingads-webui-campaign-react/with-campaign-type';
import { PerfMarkerFactory } from '@bingads-webui/performance-marker';
import { PreferenceService } from '@bingads-webui/preference-service';
import { getCldrLocale, setupI18nModel } from '@bingads-webui/i18n-model-setup';
import $ from 'jquery';
import Backbone from 'backbone';

Backbone.$ = $;

import { updateDateRange } from '@bingads-webui-campaign-react/update-date-range';
import { initGlobalService } from './global-init/global-init';
import { isDFMEnabledForPath } from './data-fetch/is-dfm-enabled-for-path';

const landingPagePreference = 'OverviewAsDefaultTab';
const accountsLandingPagePreference = 'AccountsOverviewAsDefaultTab';

// This marker would be used to track the pre app start latency.
// Any regression in this latency would impact all landing pages.
window.performance.preAppStartMarker = {
  eventName: 'ActivityMarker/AppStart.BeforeLoad',
  eventStartTime: window.performance.now(),
  activeTimeBeforeEventStart: window.performance.activeTime(),
};

// import registerServiceWorker from './register-service-worker';

// window.serverContext is specified by the server side through responded HTML.
// To avoid any code depends on window.serverContext directly,
// remove it after passing it to the <App />
const { serverContext, campaignODataConfigs } = window;

Object.assign(campaignODataConfigs, {
  AdvertiserCustomerID: serverContext.CurrentAccount.AdvertiserCustomerID,
  AccountID: serverContext.CurrentAccount.Id,
});
delete window.serverContext;

// window.devConfig only exists on local development mode
const devConfig = window.devConfig || {
  isInDevelopmentMode: false,
};

_.templateSettings = {
  interpolate: /\{\{(.+?)\}\}/g,
};

const errorMappers = {
  odataErrorMapper: new OdataErrorMapper(odataErrorCodes),
  campaignApiErrorMapper: new CampaignApiErrorMapper(campaignApiErrorCodes),
  urlErrorMapper: new UrlErrorMapper(invalidDomains),
};

const userContext = {
  CurrentUser: serverContext.CurrentUser,
  CurrentCustomer: serverContext.CurrentCustomer,
  CurrentAccount: serverContext.CurrentAccount,
};

const appName = new URLSearchParams(document.location.search).get('applicationName');
const featureName = new URLSearchParams(document.location.search).get('featurename');

const instrumentation = getInstrumentation({
  pageTrackingId: serverContext.PageTrackingId,
  errorMappers,
  isInDevelopmentMode: devConfig.isInDevelopmentMode,
  userContext,
  applicationName: {
    systemName: 'campaign',
    subSystemName: 'webui',
  },
  serverContext,
  ajaxStubOptions: {
    applicationName: appName !== null ? appName : undefined,
    featureName: featureName !== null ? featureName : undefined,
  },
});

instrumentation.addScenario(InstrumentationSchema);
const appActivity = instrumentation.appScenario.create().appActivity.create();

if (window.performance.preAppStartMarker) {
  appActivity.perf(window.performance.preAppStartMarker);
  delete window.performance.preAppStartMarker;
}

if (window.performance.preAppStartLogs) {
  const activity = instrumentation.preInstrumentationInitializeScenario.create().perfActivity.create();
  window.performance.preAppStartLogs.forEach(({ message, type }) => activity[type](message));
  delete window.performance.preAppStartLogs;
}

document.addEventListener('visibilitychange', () => {
  appActivity.trace(`Document visibility state changed to:${document.visibilityState}`, 'document-visibility-state');
});

try {
  const message = JSON.stringify({
    devicePixelRatio: window.devicePixelRatio,
    clientWidth: document.documentElement.clientWidth,
    clientHeight: document.documentElement.clientHeight,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    outerWidth: window.outerWidth,
    outerHeight: window.outerHeight,
    screenWidth: screen.width,
    screenHeight: screen.height,
    screenAvailWidth: screen.availWidth,
    screenAvailHeight: screen.availHeight,
  });

  appActivity.trace(message, 'screen-resolution-new-ui');

  // check the proportion of the AuthType
  appActivity.trace(`IsAADUser: ${serverContext.IsAADUser}, IsInternalUser: ${(serverContext.CurrentUser && serverContext.CurrentUser.IsInternal)}`, 'AuthType');
} catch (e) {
  // Do nothing, this logging is not critical.
}


const appPerfMarker = PerfMarkerFactory.createMarker('ShellPage_WebUI');

appPerfMarker.willInit();

const preferencePerfMarker = appPerfMarker.createChild('Preference_Service_Init');
preferencePerfMarker.willInit();

PreferenceService.initSync({
  initialUserPreferences: serverContext.UserPreferences,
  currentUser: serverContext.CurrentUser,
  currentCustomer: serverContext.CurrentCustomer,
  currentAccount: serverContext.CurrentAccount,
  apiHost: serverContext.ServicesEndpoints.CampaignAPI,
  apiPath: 'Advertiser/V2',
  authToken: serverContext.Token,
});

preferencePerfMarker.done();

const locale = serverContext.CurrentLocale || document.documentElement.lang;
const cldrLocale = getCldrLocale(locale);

function isDarkTheme(preferenceService) {
  if (serverContext.Permissions.IsDarkThemeEnabled) {
    const isSystemDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const themePref = preferenceService.findByName('themePreference');
    const themeObj = {
      Name: 'themePreference',
      Data: '',
    };
    if (!themePref || !themePref.Data) {
      themeObj.Data = 'system';
      preferenceService.setAtUserLevel(themeObj);
    } else {
      themeObj.Data = themePref.Data;
    }
    return themeObj.Data === 'dark' || (themeObj.Data === 'system' && isSystemDark);
  }

  return false;
}

function getTheme(preferenceService) {
  if (isDarkTheme(preferenceService)) {
    import(/* webpackChunkName: "index-dark.less" */ './index-dark.less');
    return 'dark';
  }
  import(/* webpackMode: "eager" */ './index.less');
  return 'light';
}

const curTheme = getTheme(PreferenceService);

function getLandingPath(preferenceService) {
  let loadingPath = window.location.pathname.toLowerCase();

  if (loadingPath === '/campaign/vnext') {
    const preferenceData = preferenceService.findByName(landingPagePreference);

    if (preferenceData && !preferenceData.Data) {
      loadingPath = '/campaign/vnext/campaigns';
    } else {
      loadingPath = '/campaign/vnext/overview';
    }
  } else if (loadingPath === '/campaign/vnext/accounts') {
    const preferenceData = preferenceService.findByName(accountsLandingPagePreference);

    if ((preferenceData && !preferenceData.Data)) {
      loadingPath = '/campaign/vnext/accounts/performance';
    } else {
      loadingPath = '/campaign/vnext/accounts/overview';
    }
  }

  return loadingPath;
}

function skipRequireJSLoad() {
  const isSkipRequireJsLoadEnabled = serverContext.Permissions.IsSkipRequireJsLoadEnabled;

  if (isSkipRequireJsLoadEnabled === true) {
    return import(/* webpackMode: 'eager' */'@bingads-webui-campaign-legacy/cache-manager/index');
  }

  return import(/* webpackChunkName: "vendor.async.legacy-requirejs" */'./legacy-dep-bootstrap/require.config').then(() => {
    import(/* webpackChunkName: "vendor.async.legacy-requirejs" */'./legacy-dep-bootstrap/shim/requirejs-shim');
  });
}

const es6TestScript = `
class Foo { }
const foo = new Foo();
function defaultParameter(p = '123') { }
defaultParameter();
function restParameters(...parameter) { }
restParameters(...[1, 2, 3]);
`;

try {
  // eslint-disable-next-line no-eval
  eval(es6TestScript);
} catch (error) {
  instrumentation.logError('ES6_syntax', error.toString());
}

function appBootstrap() {
  // eslint-disable-next-line global-require
  const AppRoot = require('./app').default;
  ReactDOM.render(<AppRoot
    serverContext={serverContext}
    instrumentation={instrumentation}
    appPerfMarker={appPerfMarker}
    preferenceService={PreferenceService}
    curTheme={curTheme}
  />, document.getElementById('root'));

  initGlobalService(serverContext, appActivity);

  import(/* webpackMode: 'eager' */'@bingads-webui-campaign/cache-bridge').then(({ initCacheBridge }) => initCacheBridge());
}

const params = new URLSearchParams(document.location.search);
const campaignId = params.get('campaignId');
const campaignString = _.get(serverContext, ['ODataPrefetchResult', 'campaign'], '');
const campaign = _.isEmpty(campaignString) ? null : JSON.parse(campaignString);
if (campaignId) {
  setCampaignType(Number(campaignId), _.get(campaign, 'CampaignType'));
}

skipRequireJSLoad()
  .then(() => {
    import(/* webpackChunkName: "cldr-[request]" */`@bingads-webui-universal/i18n-cldr-data-modern/locales-simplified/${cldrLocale}.json`)
      .then(setupI18nModel(locale))
      .then(() => {
        updateDateRange(PreferenceService, serverContext, appActivity);

        const landingPagePath = getLandingPath(PreferenceService);
        const isDFMEnabled = isDFMEnabledForPath(landingPagePath, serverContext, _.get(campaign, 'CampaignType'));

        if (isDFMEnabled === true) {
          const dfmPerfMarker = appPerfMarker.createChild('Data_Fetch_Manager');
          dfmPerfMarker.willFetchFile();

          Promise.all([
            import(/* webpackMode: 'eager' */'./data-fetch/data-fetch-manager'),
            import(/* webpackMode: 'eager' */'./legacy-dep-bootstrap/init-legacy-components'),
          ]).then(([
            { fetchData },
            { initLegacyComponentsWithServerContext },
          ]) => {
            initLegacyComponentsWithServerContext(serverContext, PreferenceService, curTheme);
            fetchData({
              serverContext,
              dfmPerfMarker,
              preferenceService: PreferenceService,
              loadingPath: landingPagePath,
              campaignType: _.get(campaign, 'CampaignType'),
            }).then((msg) => {
              dfmPerfMarker.done(msg);
            }).finally(() => {
              appBootstrap();
            });
          });
        } else {
          import(/* webpackMode: 'eager' */'./legacy-dep-bootstrap/init-legacy-components')
            .then(({ initLegacyComponentsWithServerContext }) => {
              initLegacyComponentsWithServerContext(serverContext, PreferenceService, curTheme);
              appBootstrap();
            });
        }
      });
  });
// registerServiceWorker();
